import type { UserRole } from '../lib/auth';

interface SidebarProps {
  currentPath: string;
  /** Lead moderators and admins also see the Issues link. */
  userRole?: UserRole;
  onNavigate: (hash: string) => void;
  /** Opens the "Report a problem" dialog. */
  onReportProblem?: () => void;
}

interface NavLink {
  hash: string;
  label: string;
  matcher: (path: string) => boolean;
  leadOrAdminOnly?: boolean;
}

const LINKS: NavLink[] = [
  {
    hash: '#/',
    label: 'Dashboard',
    matcher: (p) => p === '' || p === '#/' || p === '#',
  },
  {
    hash: '#/submissions',
    label: 'Submissions',
    matcher: (p) => p.startsWith('#/submissions'),
  },
  {
    hash: '#/activity',
    label: 'Activity Log',
    matcher: (p) => p.startsWith('#/activity'),
  },
  {
    hash: '#/issues',
    label: 'Issues',
    matcher: (p) => p.startsWith('#/issues'),
    leadOrAdminOnly: true,
  },
  {
    hash: '#/guide',
    label: 'Guide',
    matcher: (p) => p.startsWith('#/guide'),
  },
  {
    hash: '#/settings/security',
    label: 'Security',
    matcher: (p) => p.startsWith('#/settings/security'),
  },
];

export default function Sidebar({
  currentPath,
  onNavigate,
  onReportProblem,
  userRole,
}: SidebarProps) {
  const isLeadOrAdmin = userRole === 'lead_moderator' || userRole === 'admin';
  return (
    <nav className="sidebar" aria-label="Primary">
      <ul className="sidebar-list">
        {LINKS.filter((link) => !link.leadOrAdminOnly || isLeadOrAdmin).map((link) => {
          const active = link.matcher(currentPath);
          return (
            <li key={link.hash}>
              <a
                href={link.hash}
                className={`sidebar-link${active ? ' sidebar-link--active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(link.hash);
                }}
              >
                {link.label}
              </a>
            </li>
          );
        })}
      </ul>
      {onReportProblem && (
        <button
          type="button"
          className="sidebar-report report-problem-link"
          onClick={onReportProblem}
          aria-haspopup="dialog"
          data-testid="sidebar-report-problem"
        >
          Report a problem
        </button>
      )}
    </nav>
  );
}
